// src/components/intake/IntakeEvents.js
//
// Ticket 064 (screen 2.1, amendment 2). The grouped "what applied to you or your
// household" checklist plus an exclusive "None of these". Event values and labels
// come from MAJOR_EVENT_OPTIONS (sharedOptions); EVENT_GROUPS (intakeConfig) only
// chooses the groups and the order. "Owned property" is the one line that is not
// an event fact: ticking it reveals Spain / Outside Spain (both selectable) and,
// for Outside Spain, a searchable country picker (IntakeCountryPicker).
//
// `events` is null (unanswered) or { <event>: boolean }; `ownership` is null or
// { spain, outside, countries }. Nothing ticked goes back to unanswered. "None of
// these" is every event false and no ownership.

import React from 'react'
import IntakeCountryPicker from './IntakeCountryPicker'
import {
  MAJOR_EVENT_OPTIONS, EVENT_GROUPS, EVENT_NONE_LABEL, OWNED_PROPERTY_LINE, OWNED_PROPERTY_LABEL,
  OWNED_SPAIN_LABEL, OWNED_OUTSIDE_LABEL, OWNED_PROPERTY_QUESTION, OWNED_COUNTRIES_QUESTION,
  OWNED_INCOMPLETE_HINT, RENTAL_PROPERTY_PROMPT,
} from '../../lib/intake/intakeConfig'
import { ownershipComplete } from '../../lib/intake/section23Facts'

const allFalse = () => Object.fromEntries(MAJOR_EVENT_OPTIONS.map((o) => [o.value, false]))
const labelOf = (value) => MAJOR_EVENT_OPTIONS.find((o) => o.value === value).label

export default function IntakeEvents({ events, ownership, onChange }) {
  const anyEvent = !!events && MAJOR_EVENT_OPTIONS.some((o) => events[o.value])
  const none = events !== null && !anyEvent && ownership === null

  // Every change goes through here: nothing ticked at all goes back to unanswered.
  function commit(nextEvents, nextOwnership) {
    const any = MAJOR_EVENT_OPTIONS.some((o) => nextEvents[o.value]) || nextOwnership !== null
    onChange(any ? nextEvents : null, any ? nextOwnership : null)
  }
  const current = () => ({ ...allFalse(), ...(events || {}) })

  function toggleEvent(key, checked) {
    commit({ ...current(), [key]: checked }, ownership)
  }
  function toggleOwned(checked) {
    commit(current(), checked ? { spain: false, outside: false, countries: [] } : null)
  }
  const setOwnership = (patch) => commit(current(), { ...ownership, ...patch })

  return (
    <div className="intake-events u-stack-sm">
      {EVENT_GROUPS.map((group) => (
        <section key={group.label} className="intake-event-bucket" aria-label={group.label}>
          <h2 className="u-label-base">{group.label}</h2>
          <div className="intake-event-group">
          {group.lines.map((line) => {
            if (line === OWNED_PROPERTY_LINE) {
              return (
                <div key={line} className="intake-owned">
                  <label className="cal-checkbox-label intake-checkbox-label">
                    <input type="checkbox" checked={ownership !== null} onChange={(e) => toggleOwned(e.target.checked)} />
                    {OWNED_PROPERTY_LABEL}
                  </label>
                  {ownership !== null && (
                    <div className="triage-subquestion u-stack-sm">
                      <p className="triage-subquestion-label">{OWNED_PROPERTY_QUESTION}</p>
                      <label className="cal-checkbox-label intake-checkbox-label">
                        <input type="checkbox" checked={ownership.spain} onChange={(e) => setOwnership({ spain: e.target.checked })} />
                        {OWNED_SPAIN_LABEL}
                      </label>
                      <label className="cal-checkbox-label intake-checkbox-label">
                        <input
                          type="checkbox"
                          checked={ownership.outside}
                          onChange={(e) => setOwnership({ outside: e.target.checked, countries: e.target.checked ? ownership.countries : [] })}
                        />
                        {OWNED_OUTSIDE_LABEL}
                      </label>
                      {ownership.outside && (
                        <div className="u-stack-xs">
                          <p className="triage-subquestion-label">{OWNED_COUNTRIES_QUESTION}</p>
                          <IntakeCountryPicker
                            label={OWNED_COUNTRIES_QUESTION}
                            selected={ownership.countries}
                            onChange={(countries) => setOwnership({ countries })}
                          />
                        </div>
                      )}
                      {!ownershipComplete(ownership) && <p className="intake-hint-line">{OWNED_INCOMPLETE_HINT}</p>}
                    </div>
                  )}
                  {ownership === null && !!events && events.received_rental_income && (
                    <p className="intake-hint-line">{RENTAL_PROPERTY_PROMPT}</p>
                  )}
                </div>
              )
            }
            return (
              <label key={line} className="cal-checkbox-label intake-checkbox-label">
                <input type="checkbox" checked={!!events && events[line] === true} onChange={(e) => toggleEvent(line, e.target.checked)} />
                {labelOf(line)}
              </label>
            )
          })}
          </div>
        </section>
      ))}
      <label className="cal-checkbox-label intake-checkbox-label">
        <input type="checkbox" checked={none} onChange={(e) => (e.target.checked ? onChange(allFalse(), null) : onChange(null, null))} />
        {EVENT_NONE_LABEL}
      </label>
    </div>
  )
}
